import type { OnChanges, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { CardBorder, CardLevel } from '@freelancer/ui/card';
import { CheckboxColor } from '@freelancer/ui/checkbox';
import {
  BackgroundColor,
  CheckboxCardPadding,
  CheckboxCardSelectedColor,
  HoverColor,
} from './checkbox-card.types';

@Component({
  selector: 'fl-checkbox-card',
  template: `
    <fl-card
      class="CheckboxCard"
      [attr.tabindex]="1"
      [clickable]="!disabled"
      [disableFullHeight]="true"
      [edgeToEdge]="true"
      [level]="level"
      [border]="border"
      [flMarginBottom]="
        control.invalid && control.dirty ? 'xxxsmall' : undefined
      "
      [attr.data-disabled]="disabled"
      [attr.data-padding]="padding"
      [attr.data-hide-border-color]="hideBorderColor"
      [attr.data-is-selected]="control.value"
      [attr.data-selected-color]="selectedColor"
      [attr.data-background-color]="backgroundColor"
      [attr.data-hover-color]="hoverColor"
      (keyup.enter)="toggleCheckbox()"
      (click)="toggleCheckbox()"
    >
      <div>
        <div class="Container">
          <div class="Left">
            <div class="Mask"></div>
            <fl-checkbox
              class="Checkbox"
              [ariaLabel]="ariaLabel"
              [color]="checkboxColor"
              [control]="control"
              [disabled]="disabled"
              [label]="label"
              [forListItem]="true"
            ></fl-checkbox>
          </div>
          <ng-content></ng-content>
        </div>
      </div>
    </fl-card>
    <fl-validation-error
      [control]="control"
      (click)="$event.stopPropagation()"
    ></fl-validation-error>
  `,
  styleUrls: ['./checkbox-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxCardComponent implements OnChanges {
  @Input() ariaLabel?: string;
  @Input() backgroundColor?: BackgroundColor;
  @Input() border = CardBorder.NONE;
  @Input() control: FormControl<boolean>;
  @Input() disabled = false;
  @Input() hideBorderColor = false;
  @Input() hoverColor = HoverColor.MID;
  @Input() label = '';
  @Input() level = CardLevel.PRIMARY;
  @Input() padding = CheckboxCardPadding.MID;
  @Input() selectedColor?: CheckboxCardSelectedColor =
    CheckboxCardSelectedColor.SECONDARY;

  checkboxColor = CheckboxColor.SECONDARY;

  ngOnChanges(changes: SimpleChanges): void {
    if ('selectedColor' in changes) {
      switch (changes.selectedColor.currentValue) {
        case CheckboxCardSelectedColor.PRIMARY:
          this.checkboxColor = CheckboxColor.PRIMARY;
          break;
        default:
          this.checkboxColor = CheckboxColor.SECONDARY;
          break;
      }
    }
  }

  toggleCheckbox(): void {
    if (this.disabled) {
      return;
    }
    this.control.setValue(!this.control.value);
  }
}
